/**
 * @file SceneDirector.ts
 * @role Owns the active scene's lifecycle and is the ONE place per-frame updates and
 *       layout changes are routed to a scene. Subscribes directly to GameTicker + LayoutService
 *       exactly once, so swapping scenes can never leak a stale resize/update subscription
 *       onto a destroyed view. show() exits+destroys the previous scene before entering
 *       the next.
 * @owns Current scene reference; tick + layout subscriptions; scene swap ordering.
 * @reads GameTicker (clamped dt), LayoutService (current context + direct subscription).
 * @neverDoes Construct scenes (callers do). Run reel physics. Touch Pixi.Assets/HTTP.
 * @invariants 12 (previous scene exits/resets before the next enters), 13, 22 (update dt
 *             comes only from GameTicker).
 * @see docs/ARCHITECTURE.md, src/scenes/IScene.ts
 */
import type { Container } from 'pixi.js';
import type { GameTicker } from './GameTicker';
import type { LayoutService } from '@core/layout/LayoutService';
import type { IScene } from '@scenes/IScene';

export class SceneDirector {
  private current: IScene | undefined;
  private offLayout: (() => void) | undefined;
  private offTick: (() => void) | undefined;

  constructor(
    private readonly stage: Container,
    private readonly layout: LayoutService,
    private readonly ticker: GameTicker,
  ) {}

  /** Wire the single tick + layout subscriptions. Routes to whatever scene is current. */
  start(): void {
    this.offLayout = this.layout.subscribe((ctx) => this.current?.resize?.(ctx));
    this.offTick = this.ticker.add((dt) => this.current?.update?.(dt));
  }

  /**
   * Swap to a new scene: exit+destroy the previous, then preload/enter the next.
   * `current` is cleared during the transition so no tick/resize is routed to a scene
   * that has not entered yet (or one that is exiting).
   */
  async show(scene: IScene): Promise<void> {
    const previous = this.current;
    this.current = undefined;

    if (previous) {
      await previous.exit();
    }
    if (scene.preload) {
      await scene.preload();
    }
    this.stage.addChild(scene.view);
    await scene.enter();

    this.current = scene;
    scene.resize?.(this.layout.context);
  }

  get activeScene(): IScene | undefined {
    return this.current;
  }

  /** Re-apply the authoritative current layout after an optional presentation asset arrives. */
  refreshLayout(): void {
    this.current?.resize?.(this.layout.context);
  }

  /** Tear down subscriptions and exit the current scene. */
  destroy(): void {
    this.offLayout?.();
    this.offTick?.();
    this.offLayout = undefined;
    this.offTick = undefined;
    void this.current?.exit();
    this.current = undefined;
  }
}
